<template>
    <div>
        <report-header
            :title="title"
            :isLoading="isLoading"
            :reportPagination="pagination"
            :isShowDateRange="true"
            :formatDateRangeTitle="dateRange"
        />
        <b-card id="report-card">
            <div
                id="report-body"
                class="w-100 mb-4"
                v-if="providers.length > 0"
            >
                <p class="item-name">
                    Summary
                </p>
                <QTable
                    id="production-credit-by-sales-report"
                    :data="summaryResults"
                    :loading="isLoading"
                    :columns="summaryColumns"
                    :rows-per-page-options="[0]"
                    hide-bottom
                    class="q-table"
                    row-key="category"
                />

                <div
                    class="w-100"
                    v-for="(provider, i) in exportResults"
                    :key="provider.name + i"
                >
                    <p class="item-name">{{ provider.name }}</p>

                    <QTable
                        :data="provider.data"
                        :loading="isLoading"
                        :columns="formattedColumns"
                        :rows-per-page-options="[0]"
                        hide-bottom
                        class="q-table"
                        row-key="name"
                    >
                        <template v-slot:body-cell-inv_index="{ row }">
                            <td class="text-nowrap">
                                <invoice-link
                                    :href="env.APPS"
                                    :invoice="{
                                        index: row.inv_index,
                                        encoded_id: row.encoded_invoice_id
                                    }"
                                />
                            </td>
                        </template>

                        <template v-slot:body-cell-quantity="{ row }">
                            <td width="10%">
                                {{ row["quantity"] }} {{ row["uom"] }}
                            </td>
                        </template>
                    </QTable>
                </div>
            </div>
            <div v-else class="w-100 pt-2 pb-2">
                <div class="w-100" v-if="isFirstLoad && isLoading">
                    <QTable
                        :columns="getDummyColumns()"
                        :loading="true"
                        :data="[]"
                        hide-bottom
                        class="q-table"
                    />
                </div>
                <i v-else-if="message">{{ message }}</i>
                <i v-else>No records available</i>
            </div>
        </b-card>
    </div>
</template>
<script>
import "@/css/quasar-table.css";

import { QTable } from "quasar";
import { forEach, includes, isEmpty, find } from "lodash";

import Report from "@/mixins/report.js";
import CREPORT from "@/constants/report";
import ReportService from "@/services/report";
import Staff from "@/services/staff";
import InvoiceLink from "@/components/commons/invoice-link";

export default {
    components: {
        QTable,
        InvoiceLink,
        ReportHeader: () => import("@/components/report/report-header")
    },
    props: {
        data: {
            type: Object,
            default() {
                return {
                    start: this.getStartDate("month"),
                    end: this.getEndDate("day")
                };
            }
        }
    },
    mixins: [Report],
    data() {
        return {
            result: [],
            tableHeader: [],
            dateRange: "",
            isLoading: false,
            pageSize: 20,
            lastPage: 0,
            pagination: {},
            columns: [],
            items: [],
            isFirstLoad: true,
            refresh: 0,
            title: "Production Credit Earned (Based on Sales)",
            providers: [],
            summaryResults: [],
            exportResults: [],
            results: null,
            message: null,
            abortController: [],
            indexAxios: 0
        };
    },
    computed: {
        formattedColumns() {
            let self = this;
            let columns = [];

            forEach(self.results.field_index, function (header, index) {
                let classes = includes(["date", "inv_index"], header)
                    ? "text-nowrap width-10-pc"
                    : "";

                if (header === "billed") {
                    columns.push({
                        name: header,
                        label: self.results.fields_names[index],
                        field: header,
                        format: (val, row) => {
                            let billed = row["billed"]
                                ? Number(row["billed"])
                                : 0;
                            return self._toCurrency(billed);
                        },
                        align: "center",
                        classes,
                        style: "width: 10%;"
                    });
                } else if (header === "amount") {
                    columns.push({
                        name: header,
                        label: self.results.fields_names[index],
                        field: header,
                        format: (val, row) => {
                            let amount = row["amount"]
                                ? Number(row["amount"])
                                : 0;
                            return self._toCurrency(amount);
                        },
                        align: "center",
                        classes,
                        style: "width: 10%;"
                    });
                } else if (header === "percent") {
                    columns.push({
                        name: header,
                        label: self.results.fields_names[index],
                        field: header,
                        align: "center",
                        classes
                    });
                } else {
                    columns.push({
                        name: header,
                        label: self.results.fields_names[index],
                        field: header,
                        align: "left",
                        classes
                    });
                }
            });

            return columns;
        },
        summaryColumns() {
            let self = this;
            let columns = [
                {
                    name: "category",
                    label: "Category (Type)",
                    field: "category",
                    align: "left"
                }
            ];

            forEach(this.providers, function (provider) {
                columns.push({
                    name: provider,
                    label: provider,
                    align: "center",
                    field: row => row[provider],
                    format: (val, row) => {
                        let data = 0;

                        if (row.category === "Total (Exc. Tax)") {
                            data =
                                self.results.pivor_total_by_provider[provider];
                        } else {
                            data =
                                self.results &&
                                self.results.summary[provider][row.category]
                                    ? self.results.summary[provider][
                                          row.category
                                      ]
                                    : 0;
                        }

                        return self._toCurrency(data);
                    }
                });
            });

            columns.push({
                name: "total",
                label: "Total (Exc. Tax)",
                field: "total",
                align: "center",
                style: "font-weight: bold; width: 10%;",
                format: val => {
                    return self._toCurrency(val);
                }
            });

            return columns;
        }
    },
    mounted() {
        let self = this;
        // --- toggle navigation options
        self.data.isShowDateRange = true;
        self.data.isShowProviderOptions = true;
        self.data.isShowItemClass = true;
        self.data.isShowItemCategory = true;
        self.data.isShowAllSalesCategory = 0;
        self.data.isIncludeRefillSales = 1;
        self.data.isExcludeEligibilityFee = 0;
        self.data.reportId = CREPORT.TYPE.PRODUCTION_CREDIT_BY_SALES;

        self._getSetting().then(() => {
            self.initSetDates().then(() => {
                self.toggleOptions(false);
                self.init();
            });
        });
        self.$root.$on("report-filter-date-range:refresh", self.prepareData);
        self.$root.$on("sales-production-credit-detail:refresh", function () {
            if (self.abortController.length > 0) {
                self.abortController[self.indexAxios].abort();
            }
            self.indexAxios++;
            self.resultProviders = [];
            self.summaryData = [];
            self.toggleOptions(false);
            self.resetPagination();
            self.init(self.indexAxios, 1);
        });

        window.scrollTo(0, 0);
    },
    beforeDestroy: function () {
        this.data.isShowDateTimeRange = false;
        this.data.isShowProviderOptions = false;
        this.data.isShowItemClass = false;
        this.data.isShowItemCategory = false;
        this.data.reportId = null;

        this.$root.$off("report-filter-date-range:refresh", this.prepareData);
        this.$root.$off("sales-production-credit-detail:refresh");
        if (this.abortController.length > 0) {
            this.abortController[this.indexAxios].abort();
        }
    },
    methods: {
        prepareData(params) {
            if (this.abortController.length > 0) {
                this.abortController[this.indexAxios].abort();
            }
            if (params) {
                this.data.from = params.from;
                this.data.to = params.to;
                this.refresh = params.refresh;
                this.data.itemClass = params.itemClass;
                this.data.isShowAllSalesCategory =
                    params.isShowAllSalesCategory;
                this.data.isIncludeRefillSales = params.isIncludeRefillSales;
                this.data.isExcludeEligibilityFee = params.isExcludeEligibilityFee;
                this.data.category =
                    params.category.length > 0 ? params.category : [];
                this.data.providerShowMode = params.providerShowMode;
                if (
                    params.providerShowMode === 2 &&
                    params.provider &&
                    params.provider.length > 0
                ) {
                    this.data.provider = params.provider;
                } else {
                    this.data.provider = [];
                }
            }
            this.indexAxios++;
            this.toggleOptions(false);
            this.resetPagination();
            this.init(this.indexAxios, 2);
        },
        toggleOptions(value) {
            this.defaultToggleOptions(value);
        },
        async init(indexAxios, isRefresh = 0) {
            let self = this;
            let category = [];
            let salesProviderData = [];

            self.isFirstLoad = self.providers.length === 0;
            self.isLoading = true;
            self.message = null;
            self.dateRange = self.getDateRange(self.data.from, self.data.to);
            self.abortController[indexAxios] = new AbortController();
            if (self.data?.provider?.length > 0) {
                salesProviderData = self.data?.provider.map(
                    provider => provider.value
                );
            } else {
                salesProviderData = [];
            }

            await ReportService.getReport(
                CREPORT.TYPE.PRODUCTION_CREDIT_BY_SALES,
                {
                    from: self.data.from,
                    to: self.data.to,
                    refresh: isRefresh,
                    item_class: self.data.itemClass,
                    category: self.data.category,
                    provider: salesProviderData,
                    show_results: false,
                    all_sales_category: self.data.isShowAllSalesCategory,
                    include_refill_sales: self.data.isIncludeRefillSales,
                    exclude_eligibility_fee: self.data.isExcludeEligibilityFee,
                }
            )
                .then(res => {
                    if (res && res.message && res.message.code === 200) {
                        const results = res.resource.results;
                        self.results = res.resource.results;

                        self.tableHeader = results.fields_names;
                        self.providers = self.getProviders(
                            results.pivor_total_by_provider
                        );
                        self.summaryResults = self.getSummaryResults(
                            results.pivor_total_by_item_type_category
                        );
                        self.exportResults = [];
                        if (self.providers.length > 0) {
                            self.getProviderDetails(indexAxios, 1, isRefresh, res.isqueue);
                        } else {
                            self.toggleOptions(true);
                            self.isLoading = false;
                        }
                        self.dynamicPageTitle(res.resource.meta.title);
                    } else if (res && res.queue) {
                        let dateTo = self.data.to;
                        let dateFrom = self.data.from;
                        let query = self.$route.query;

                        self._message(res.message, "warning");

                        if (
                            !(
                                query &&
                                query.to == dateTo &&
                                query.from == dateFrom
                            )
                        ) {
                            self.$router.replace({
                                query: { to: dateTo, from: dateFrom }
                            });
                        }
                        self.toggleOptions(true);
                    } else {
                        self._message(
                            "Error: Unable to get the report",
                            "danger"
                        );
                    }
                })
                .catch(e => {
                    self._message("Error: Unable to get the report", "danger");
                    console.error(e);
                    self.isLoading = false;
                    self.toggleOptions(true);
                })
                .finally(() => {
                    self.isFirstLoad = false;
                    self.isLoading = false;
                });
        },
        async initSetDates() {
            let query = this.$route.query;
            let self = this;

            if (!isEmpty(query.to) && !isEmpty(query.from)) {
                self.data.datePickerOptionDefault = 1;
                self.data.provider = [];
                self.data.start = moment.unix(query.from).tz(self._timeZone);
                self.data.end = moment.unix(query.to).tz(self._timeZone);
                self.data.from = query.from;
                self.data.to = query.to;
                self.dateRange = self.getDateRange(query.from, query.to);
                self.data.isShowAllSalesCategory = query.all_sales_category;
                self.data.itemClass = query.item_class;
                self.data.isIncludeRefillSales = query.include_refill_sales;
                self.data.isExcludeEligibilityFee = query.exclude_eligibility_fee;
                if (!isEmpty(query.category)) {
                    const categories = [];
                    forEach(JSON.parse(query.category), async function (
                        category
                    ) {
                        categories.push(category);
                    });
                    self.data.category = categories;
                }
                if (!isEmpty(query.provider)) {
                    const salesProvidersData = [];

                    forEach(
                        JSON.parse(query.provider),
                        async function (eachProvider) {
                            salesProvidersData.push(eachProvider);
                        }
                    );
                    if (salesProvidersData.length > 0) {
                        await Staff.getProviders().then(response => {
                            response.resource.data.forEach(element => {
                                if (
                                    salesProvidersData.includes(
                                        element.actor.id
                                    )
                                ) {
                                    self.data.provider.push({
                                        value: element.actor.id,
                                        name:
                                            element.actor.firstname +
                                            " " +
                                            element.actor.lastname
                                    });
                                }
                            });
                        });
                    }
                    self.data.providerShowMode = 2;
                }
            } else {
                self.data.start = self.getStartDate("isoWeek");
                self.data.end = self.getEndDate("day");
                self.data.from = self.converToUnix(self.data.start);
                self.data.to = self.converToUnix(self.data.end);
            }
        },
        async getProviderDetails(indexAxios, page = 1, isRefresh, isQueue = false) {
            let self = this;
            self.pagination.page = page;
            self.loadingPercentages();
            self.isFirstLoad = self.providers.length === 0;
            self.isLoading = true;
            self.message = null;
            let salesProviderData = self.data?.provider
                ? self.data.provider.map(provider => provider.value)
                : [];

            await ReportService.getReport(
                CREPORT.TYPE.PRODUCTION_CREDIT_BY_SALES,
                {
                    from: self.data.from,
                    to: self.data.to,
                    item_class: self.data.itemClass,
                    category: self.data.category,
                    provider: salesProviderData,
                    page: self.pagination.page,
                    perPage: isQueue == true ? 500 : 100,
                    signal: self.abortController[indexAxios].signal,
                    show_results: true,
                    refresh: isRefresh,
                    all_sales_category: self.data.isShowAllSalesCategory,
                    include_refill_sales: self.data.isIncludeRefillSales,
                    exclude_eligibility_fee: self.data.isExcludeEligibilityFee
                }
            )
                .then(res => {
                    if (res && res.message && res.message.code == 200) {
                        forEach(res.resource.results.exports, function (v) {
                            let providerObject = find(self.exportResults, {
                                name: v.provider
                            });
                            if (!providerObject) {
                                self.exportResults.push({
                                    name: v.provider,
                                    data: [v]
                                });
                            } else {
                                providerObject.data.push(v);
                            }
                        });

                        self.pagination.lastPage =
                            res.resource.results.meta.lastPage;

                        if (self.pagination.page < self.pagination.lastPage) {
                            self.getProviderDetails(
                                indexAxios,
                                ++self.pagination.page,
                                isRefresh,
                                isQueue
                            );
                        } else {
                            self.loadingCompleted();
                            self.timer(1000).then(() => {
                                self.toggleOptions(true);
                                self.isLoading = false;
                            });
                        }
                    } else {
                        throw new Error("Error: Unable to get the report.");
                    }
                })
                .catch(e => {
                    if (!self.abortController[indexAxios].signal.aborted) {
                        console.error(e.message);
                        self.isLoading = false;
                        self.loadingCompleted();
                        self.toggleOptions(true);
                        self.isError = true;
                        self._message(
                            "Error: Unable to get the report",
                            "danger"
                        );
                    }
                })
                .finally(() => {
                    self.dynamicPageTitle(self.title);
                    self.isFirstLoad = false;
                });
        },
        getSummaryResults(totalCategory) {
            let results = [];

            forEach(totalCategory, function (val, key) {
                results.push({
                    category: key,
                    total: val
                });
            });

            results.push({
                category: "Total (Exc. Tax)",
                total: this.results.grandTotal
            });

            return results;
        },
        getProviders(totalByProvider) {
            let providers = [];

            forEach(totalByProvider, function (val, key) {
                providers.push(key);
            });

            return providers;
        },
        getDummyColumns() {
            return [
                { name: "category", label: "Category", align: "left" },
                { name: "provider", label: " ... ", align: "left" },
                { name: "total", label: "Total (Exc.Tax)", align: "left" }
            ];
        }
    }
};
</script>
<style type="text/css">
#production-credit-by-sales-report table tbody tr:last-child {
    font-weight: bold !important;
}

.width-10-pc {
    width: 10%;
}
</style>
